import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Quote } from "lucide-react";
import { SERVICES } from "@/components/of/WhatWeDo";
import { SERVICE_CONTENT } from "@/data/serviceContent";
import Header from "@/components/of/Header";
import Footer from "@/components/of/Footer";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.gtag) window.gtag('event', 'service_page_view', { service: slug });
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const others = SERVICES.filter((s) => s.slug !== slug);
  const content = SERVICE_CONTENT[slug];

  return (
    <main data-testid={`service-page-${slug}`} className="bg-white text-[#0A0A0A]">
      <Header />

      {/* HERO */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 noise relative">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <Link
            to="/#approach"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-[#6B7280] hover:text-[#0052FF] mb-10"
          >
            <ArrowLeft size={14} /> All services
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-sm text-[#6B7280]">{service.n}</span>
            <span className="overline">Service</span>
            <Icon size={20} className="text-[#0052FF]" strokeWidth={1.5} />
          </div>

          <h1
            data-testid="service-title"
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-8"
          >
            {service.title}
          </h1>

          {content ? (
            <p
              data-testid="service-tagline"
              className="text-2xl md:text-3xl text-[#0A0A0A] leading-snug max-w-2xl mb-2 font-display font-medium"
            >
              {content.hero.tagline}
            </p>
          ) : (
            <p className="text-xl md:text-2xl text-[#4B5563] leading-snug max-w-2xl mb-12">
              {service.blurb}
            </p>
          )}

          {!content && (
            <div className="bg-[#FDE047] border-2 border-[#0A0A0A] p-5 md:p-6 inline-flex items-center gap-3 mt-10">
              <span className="font-mono text-xs uppercase tracking-wider">Coming soon</span>
              <span className="text-sm text-[#0A0A0A]/80">
                Detailed page in development. In the meantime,{" "}
                <a href="/#contact" className="font-medium link-ink">talk to us</a>.
              </span>
            </div>
          )}
        </div>
      </section>

      {content && <RichServiceBody content={content} />}

      {/* OTHER SERVICES */}
      <section className="py-20 md:py-24 bg-[#F3F4F6] border-t border-black/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <p className="overline mb-6">Other services</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-black/15">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`other-service-${s.slug}`}
                  className="group border-r border-b border-black/15 p-7 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-start justify-between gap-6"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-mono text-xs text-[#6B7280] group-hover:text-white/60">{s.n}</span>
                      <OIcon size={16} className="text-[#0052FF] group-hover:text-[#FDE047]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display font-bold text-xl tracking-tight mb-2">{s.title}</h3>
                    <p className="text-sm text-[#4B5563] group-hover:text-white/70 leading-relaxed">{s.blurb}</p>
                  </div>
                  <ArrowUpRight size={20} className="shrink-0 mt-1 text-[#0A0A0A] group-hover:text-[#FDE047] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function RichServiceBody({ content }) {
  const { caseStudy, testimonial } = content;
  return (
    <>
      {/* INTRO */}
      <section
        data-testid="service-intro"
        className="py-20 md:py-28 bg-white border-t border-black/10"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline mb-4">Intro</p>
              <p className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-[1.05]">
                {content.intro.lead}
              </p>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-6">
              {content.intro.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-lg md:text-xl text-[#4B5563] leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <CapabilitiesGrid capabilities={content.capabilities} />

      {/* CASE STUDY + TESTIMONIAL (optional) */}
      {caseStudy && (
        <CaseStudyBlock caseStudy={caseStudy} testimonial={testimonial} />
      )}

      {/* STANDALONE TESTIMONIAL when there is a testimonial but no case study */}
      {!caseStudy && testimonial && (
        <StandaloneTestimonial testimonial={testimonial} />
      )}

      {/* CTA */}
      <CtaBlock cta={content.cta} />
    </>
  );
}

function CapabilitiesGrid({ capabilities }) {
  const items = capabilities.items;
  // Pad the grid so the last row always fills out 3 columns on lg.
  const fillerCount = (3 - (items.length % 3)) % 3;
  return (
    <section
      id="capabilities"
      data-testid="service-capabilities"
      className="py-24 md:py-32 bg-[#F3F4F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-4">{capabilities.kicker}</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1]">
              {capabilities.title}
            </h2>
          </div>
          <p className="text-[#4B5563] max-w-md">{capabilities.sub}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-black/15 bg-white">
          {items.map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <div
                key={item.name}
                data-testid={`capability-${i}`}
                className="group relative border-r border-b border-black/15 p-7 md:p-8 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-mono text-xs text-[#6B7280] group-hover:text-white/50">
                    / {String(i + 1).padStart(2, "0")}
                  </span>
                  <ItemIcon
                    size={22}
                    strokeWidth={1.5}
                    className="text-[#0052FF] group-hover:text-[#FDE047]"
                  />
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-2 leading-tight">
                  {item.name}
                </h3>
                <p className="text-sm md:text-base text-[#4B5563] group-hover:text-white/70 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            );
          })}
          {/* Ghost cells to complete the 3-col grid on lg */}
          {Array.from({ length: fillerCount }).map((_, i) => (
            <div
              key={`filler-${i}`}
              aria-hidden="true"
              className="hidden lg:block border-r border-b border-black/15 bg-[#F9FAFB]"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyBlock({ caseStudy, testimonial }) {
  const hasStats = Array.isArray(caseStudy.stats) && caseStudy.stats.length > 0;
  return (
    <section
      data-testid="service-case-study"
      className="py-24 md:py-32 bg-[#0A0A0A] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left — narrative */}
          <div className="lg:col-span-6">
            <p className="overline mb-5 text-[#FDE047]">{caseStudy.eyebrow}</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] mb-8 whitespace-pre-line">
              {caseStudy.title}
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mb-8 max-w-lg">
              {caseStudy.body}
            </p>
            <div className="flex flex-wrap gap-3">
              {caseStudy.tags.map((t) => (
                <span
                  key={t}
                  className="border border-white/20 px-3 py-1.5 text-sm font-mono"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* Right — stats (if any) + testimonial (if any) */}
          <div className="lg:col-span-6">
            {hasStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-white/15">
                {caseStudy.stats.map((s, i) => (
                  <div
                    key={s.label}
                    data-testid={`case-stat-${i}`}
                    className={`p-7 md:p-8 ${i % 2 === 1 ? "bg-[#0052FF]" : ""} ${
                      i < caseStudy.stats.length - 1
                        ? "md:border-r border-white/15 border-b md:border-b-0"
                        : ""
                    }`}
                  >
                    <div className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-none mb-3">
                      {s.number}
                    </div>
                    <div className="text-xs md:text-sm uppercase tracking-wider text-white/70 font-mono">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {testimonial && (
              <TestimonialCard
                testimonial={testimonial}
                className={hasStats ? "mt-8" : ""}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StandaloneTestimonial({ testimonial }) {
  return (
    <section
      data-testid="service-testimonial-standalone"
      className="py-20 md:py-28 bg-[#0A0A0A] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
      <div className="relative max-w-[900px] mx-auto px-6 md:px-10 lg:px-14">
        <p className="overline mb-5 text-[#FDE047]">Client voice</p>
        <TestimonialCard testimonial={testimonial} />
      </div>
    </section>
  );
}

function TestimonialCard({ testimonial, className = "" }) {
  return (
    <figure
      data-testid="service-testimonial"
      className={`p-8 md:p-10 bg-white text-[#0A0A0A] relative ${className}`}
    >
      <Quote
        size={36}
        strokeWidth={1.5}
        className="absolute top-6 right-6 text-[#0052FF]"
        aria-hidden="true"
      />
      <blockquote className="font-display font-medium text-lg md:text-xl leading-snug tracking-tight mb-6 pr-8">
        <span
          className="text-[#0052FF] font-display font-black text-3xl leading-none mr-0.5"
          aria-hidden="true"
        >
          “
        </span>
        {testimonial.quote}
        <span
          className="text-[#0052FF] font-display font-black text-3xl leading-none ml-0.5"
          aria-hidden="true"
        >
          ”
        </span>
      </blockquote>
      <figcaption className="flex items-center gap-4 pt-5 border-t border-black/10">
        <div className="w-11 h-11 flex items-center justify-center font-display font-black text-sm shrink-0 bg-[#0A0A0A] text-white">
          {testimonial.initials}
        </div>
        <div className="leading-tight">
          <div className="font-display font-bold text-base text-[#0A0A0A]">
            {testimonial.name}
          </div>
          <div className="font-mono text-[0.7rem] uppercase tracking-wider mt-0.5 text-[#6B7280]">
            {testimonial.org}
          </div>
        </div>
      </figcaption>
    </figure>
  );
}

function CtaBlock({ cta }) {
  // Underline the last 2 words of the title for a consistent yellow accent.
  const parts = cta.title.split(" ");
  const head = parts.slice(0, -2).join(" ");
  const tail = parts.slice(-2).join(" ");
  return (
    <section
      data-testid="service-cta"
      className="py-24 md:py-32 bg-white border-t border-black/10"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-4">Next step</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] mb-6">
              {head}{head && " "}
              <span className="relative inline-block">
                <span className="relative z-10">{tail}</span>
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#FDE047] -z-0" />
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-[#4B5563] leading-relaxed mb-8">{cta.sub}</p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/#contact"
                data-testid="service-cta-primary"
                className="group bg-[#0A0A0A] text-white px-6 py-3.5 font-medium inline-flex items-center gap-2 hover:bg-[#0052FF] transition-colors"
              >
                {cta.primary}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/#approach"
                data-testid="service-cta-secondary"
                className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-6 py-3.5 font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {cta.secondary}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
